/**
 * v3.dev — DEV-ONLY tooling endpoints. SuperAdmin role + the
 * `dev.mode.enabled` SystemSetting are required server-side; these wrappers
 * never see production unless an admin has flipped the flag.
 *
 * Bill 2026-05-09 — supports the OFAC Rubric Runner admin tool. Routes a
 * Treasury SLS entity-id lookup through the Function so the browser never
 * has to fight with XML / CORS, and the auth gate stays centralized.
 */

import type { ApiHttpClient } from '../http.js';

/** Single SDN-list name entry (primary or AKA). */
export interface OfacSlsName {
  /** True for the entity's primary name; false for AKA / FKA / NKA. */
  isPrimary: boolean;
  /** "A.K.A." / "F.K.A." / "N.K.A." when isPrimary=false; undefined otherwise. */
  aliasType?: string | null;
  /** "MUZZAMIL, Mohammad Daoud" — the canonical formattedFullName. */
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface OfacSlsAddress {
  country?: string | null;
}

export interface OfacSlsFeature {
  /** "Birthdate" / "Gender" / "Place of Birth" / "Citizenship" / etc. */
  type?: string | null;
  value?: string | null;
}

/** Cleaned-up Treasury SLS entity record. */
export interface OfacSlsEntity {
  /** SLS entity-id (matches JHA OFACSrch AliasId when the mapping holds). */
  id?: string | null;
  /** "Individual" / "Entity" / "Vessel" / "Aircraft". */
  entityType?: string | null;
  /** Convenience — `formattedFullName` of the primary <name>. */
  primaryName?: string | null;
  /** Lists the entity appears on — typically just "SDN List". */
  sanctionsLists: ReadonlyArray<string>;
  /** Programs (SDGT / IRAN / CUBA / RUSSIA-EO14024 / etc.) — drives sanction scope. */
  programs: ReadonlyArray<string>;
  /** Legal authorities (EO citations, public laws). */
  legalAuthorities: ReadonlyArray<string>;
  /** All names (primary + AKAs). */
  names: ReadonlyArray<OfacSlsName>;
  addresses: ReadonlyArray<OfacSlsAddress>;
  features: ReadonlyArray<OfacSlsFeature>;
  /** Convenience extracts from `features` (first match wins). */
  dateOfBirth?: string | null;
  gender?: string | null;
  placeOfBirth?: string | null;
}

export interface OfacSlsLookupResponse {
  success: boolean;
  /** False when the upstream returned no <entity> element. */
  found: boolean;
  /** Echoed back — the AliasId we asked for. */
  aliasId: string;
  /** Always 'sls' when this wrapper is the source. */
  source?: 'sls';
  /** Present when `found === true`. */
  entity?: OfacSlsEntity;
  /** Free-text note when found=false. */
  note?: string;
  /** When success=false. */
  error?: string;
  code?: string;
  upstreamStatus?: number;
}

export class DevApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Look up a Treasury SLS entity by its entity-id (= JHA OFACSrch AliasId
   * if the ID-space mapping holds for that hit).
   *
   * Server gate: SuperAdmin + SystemSetting `dev.mode.enabled` = true.
   * The endpoint returns 200 with `{found:false}` when SLS responded but had
   * no entity — the rubric runner uses that as the "FAIL — mapping broke"
   * signal without throwing.
   */
  public ofacSls(aliasId: string | number): Promise<OfacSlsLookupResponse> {
    const id = String(aliasId).trim();
    return this.http.get<OfacSlsLookupResponse>(`/v3/dev/ofac-sls/${encodeURIComponent(id)}`);
  }
}
